import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { CancelTokenSource } from "axios";

import { translateMessage } from "src/app/methods/translateMessage";
import { Metadata } from "src/app/Task/Subtask/Subtask";
import { RootState } from "src/redux/reducers";
import { SearchResultsService } from "src/services";
import {
  ProjectSearchResult,
  ReactSelectOptionWithReactNodeLabel,
} from "src/types";
import SearchSelectDropdown from "src/app/dropdowns/SearchSelectDropdown/SearchSelectDropdown";
import useGetProjectsSearchResults from "src/hooks/useGetProjectsSearchResults";
import SelectOption from "src/app/components/SelectOption/SelectOption";
import campaignPlaceholder from "src/images/project-placeholder.svg";

interface ActivityProjectFilter {
  [key: string]: {
    value?: string[];
  };
}
interface Props {
  col: Metadata;
  sendFilter: (colUuid: string, value: any) => void;
  filters: Partial<ActivityProjectFilter>;
  labelHtmlFor: string;
}

function ProjectSelectorFilter({
  col,
  sendFilter,
  filters,
  labelHtmlFor,
}: Props) {
  const [selectedOptions, setSelectedOptions] = useState<
    ReactSelectOptionWithReactNodeLabel[] | null
  >(null);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const intl = useIntl();

  const [projectsSearchResultsState, initializeProjectsSearchResultsFetch] =
    useGetProjectsSearchResults();

  const generateOptions = (
    options: ProjectSearchResult[],
  ): ReactSelectOptionWithReactNodeLabel[] => {
    return options.map((project) => ({
      value: project.wsProjectUuid,
      label: (
        <SelectOption
          name={project.wsProjectName}
          imageSource={
            project.projectCover?.thumbnails.tiny ?? campaignPlaceholder
          }
        />
      ),
    }));
  };

  const onChange = (newOptions: ReactSelectOptionWithReactNodeLabel[]) => {
    setSelectedOptions(newOptions);

    const updatedFilterValues = newOptions.map((o) => o.value);

    sendFilter(col.uuid, updatedFilterValues);
  };

  const handleLoadOptions = async (
    inputValue: string,
    cancelToken: CancelTokenSource,
  ) => {
    const data = await SearchResultsService.getProjects(
      activeWorkspaceUuid,
      [inputValue],
      cancelToken,
    );
    return generateOptions(data);
  };

  useEffect(() => {
    const projectFilter = filters[col.uuid]?.value;
    if (!projectFilter) return;

    if (projectFilter.length > 0) {
      initializeProjectsSearchResultsFetch(activeWorkspaceUuid, projectFilter);
    }
  }, []);

  useEffect(() => {
    if (projectsSearchResultsState.type === "done") {
      setSelectedOptions(generateOptions(projectsSearchResultsState.data));
    }
  }, [projectsSearchResultsState]);

  useEffect(() => {
    const projectFilter = filters[col.uuid]?.value;
    if (!projectFilter) return;

    if (projectFilter.length === 0) {
      setSelectedOptions([]);
    }
  }, [filters, col.uuid, activeWorkspaceUuid]);

  return (
    <SearchSelectDropdown<ReactSelectOptionWithReactNodeLabel>
      id={labelHtmlFor}
      value={selectedOptions}
      handleChange={onChange}
      placeholder={translateMessage({
        intl,
        id: "ws_search_campaign",
        defaultMessage: "Type to find a campaign",
      })}
      fetchOptions={handleLoadOptions}
      isMulti
      withTiles
      openMenuOnFocus={false}
    />
  );
}

export default ProjectSelectorFilter;
